import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronsUpDown, Check, Calendar, MapPin } from 'lucide-react'
import { useAuthStore, type ActivePerformance } from '@/lib/auth-store'
import { artistFetch } from '@/lib/artist-api'
import { cn } from '@/lib/utils'

interface TourRow {
  id: string
  name: string
  startDate?: string | null
  endDate?: string | null
}

interface ShowRow {
  id: string
  title?: string | null
  venue?: string | null
  city?: string | null
  country?: string | null
  date?: string | null
  tourId?: string | null
}

interface UnifiedPerformance {
  kind: 'tour' | 'show'
  id: string
  name: string
  subline: string | null
}

function formatDate(iso?: string | null): string | null {
  if (!iso) return null
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
  } catch {
    return null
  }
}

/**
 * Sidebar context chip — picks which tour OR standalone show the 9-stage
 * nav scope to. Client-side only: writes to auth-store.activePerformance.
 * Persisted via Zustand `persist` middleware so the selection survives
 * reloads.
 */
export function PerformanceSwitcher() {
  const user = useAuthStore((s) => s.user)
  const active = useAuthStore((s) => s.activePerformance)
  const setActive = useAuthStore((s) => s.setActivePerformance)
  const [open, setOpen] = useState(false)

  const enabled = !!user?.artistId

  const tours = useQuery<TourRow[]>({
    queryKey: ['perf-switcher-tours', user?.artistId],
    queryFn: () => artistFetch<TourRow[]>('/tours'),
    enabled,
  })
  const shows = useQuery<ShowRow[]>({
    queryKey: ['perf-switcher-shows', user?.artistId],
    queryFn: () => artistFetch<ShowRow[]>('/shows'),
    enabled,
  })

  if (!enabled) return null

  const tourOptions: UnifiedPerformance[] = (tours.data ?? []).map((t) => ({
    kind: 'tour',
    id: t.id,
    name: t.name,
    subline:
      t.startDate && t.endDate
        ? `${formatDate(t.startDate)} – ${formatDate(t.endDate)}`
        : t.startDate
          ? `From ${formatDate(t.startDate)}`
          : null,
  }))
  const showOptions: UnifiedPerformance[] = (shows.data ?? [])
    .filter((s) => !s.tourId) // standalone only
    .map((s) => ({
      kind: 'show',
      id: s.id,
      name: s.title ?? s.venue ?? 'Untitled show',
      subline: [formatDate(s.date), [s.city, s.country].filter(Boolean).join(', ')]
        .filter(Boolean)
        .join(' · ') || null,
    }))

  function pick(perf: UnifiedPerformance) {
    const payload: ActivePerformance = { kind: perf.kind, id: perf.id, name: perf.name }
    setActive(payload)
    setOpen(false)
  }

  function clear() {
    setActive(null)
    setOpen(false)
  }

  const loading = tours.isLoading || shows.isLoading

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 rounded-md border border-ovation-ink/15 bg-white px-3 py-2 text-left text-sm hover:border-ovation-ink/30"
      >
        <div className="min-w-0">
          <div className="truncate text-[10px] uppercase tracking-wider text-ovation-muted">
            Working on
          </div>
          <div className="truncate font-medium">
            {active?.name ?? 'No performance selected'}
          </div>
        </div>
        <ChevronsUpDown className="h-4 w-4 text-ovation-muted" />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 max-h-80 w-full overflow-auto rounded-md border border-ovation-ink/15 bg-white shadow-lg">
          {loading && (
            <div className="px-3 py-2 text-xs text-ovation-muted">Loading…</div>
          )}
          {!loading && tourOptions.length === 0 && showOptions.length === 0 && (
            <div className="px-3 py-2 text-xs text-ovation-muted">No tours or shows yet.</div>
          )}

          {active && (
            <button
              type="button"
              onClick={clear}
              className="block w-full border-b border-ovation-ink/5 px-3 py-2 text-left text-xs text-ovation-muted hover:bg-ovation-ink/5"
            >
              Clear selection
            </button>
          )}

          {tourOptions.length > 0 && (
            <div className="border-b border-ovation-ink/5 px-3 py-1 text-[10px] uppercase tracking-wider text-ovation-muted">
              <Calendar className="mr-1 inline h-3 w-3" /> Tours
            </div>
          )}
          {tourOptions.map((t) => (
            <PerfRow key={`tour-${t.id}`} perf={t} active={active} onPick={pick} />
          ))}

          {showOptions.length > 0 && (
            <div className="border-b border-ovation-ink/5 px-3 py-1 text-[10px] uppercase tracking-wider text-ovation-muted">
              <MapPin className="mr-1 inline h-3 w-3" /> Standalone shows
            </div>
          )}
          {showOptions.map((s) => (
            <PerfRow key={`show-${s.id}`} perf={s} active={active} onPick={pick} />
          ))}
        </div>
      )}
    </div>
  )
}

function PerfRow({
  perf,
  active,
  onPick,
}: {
  perf: UnifiedPerformance
  active: ActivePerformance | null
  onPick: (p: UnifiedPerformance) => void
}) {
  const isActive = active?.kind === perf.kind && active.id === perf.id
  return (
    <button
      type="button"
      onClick={() => onPick(perf)}
      className={cn(
        'flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-ovation-ink/5',
        isActive && 'bg-ovation-ink/5',
      )}
    >
      <div className="min-w-0">
        <div className="truncate">{perf.name}</div>
        {perf.subline && (
          <div className="truncate text-[10px] uppercase tracking-wider text-ovation-muted">
            {perf.subline}
          </div>
        )}
      </div>
      {isActive && <Check className="h-4 w-4 flex-shrink-0 text-emerald-600" />}
    </button>
  )
}
